import codegen from 'codegen.macro';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  BrowserRouter as Router,
  Route,
  Redirect,
  withRouter,
  Switch,
  useLocation,
} from 'react-router-dom';
import { Redirector } from 'react-router-redirect';
import { Route404, Global404, AppLoading } from 'ui-components/src';

import { useSelector } from 'react-redux';
import { initialiseEnv } from 'ui-common/src/lib/utils';
import Admin from 'ui-admin';
import { PAMRoutes } from '@saviynt/pam';
import { CertsRoutes } from '@saviynt/certs';
import { AAGRoutes, AAG_SOD_BASENAME, AAG_HEADER_URLS } from '@saviynt/aag';
import { isLoggedIn } from '@saviynt/common';
import { ExtRoutes } from '@saviynt/external';
import { RuleRecRoutes } from '@saviynt/rule-recs';
import { mfaPrompt } from '@saviynt/nav';
import { Login } from '../features/MainLayout/Login';
import { RequestHome } from '../features/MainLayout/RequestHome';
// eslint-disable-next-line
import { getModule } from './dynamic-modules';
import { Footer } from '../features/MainLayout/Footer';
import { Control } from '../features/MainLayout/Control';
import AuthRedirect from '../auth/AuthRedirect';
import UserLogout from '../logout/UserLogout';
import SomethingWentWrong from '../logout/SomethingWentWrong';
import SessionTimeout from '../logout/SessionTimeout';
import VersionPage from '../features/MainLayout/Version';
import Layout from '../Layout/Layout';
import LayoutWrapper from '../Layout/LayoutWrapper/LayoutWrapper';
import { Header } from '../features/MainLayout/Header';
import BrandingConfig from '../features/AdminHome/views/BrandingConfig';
import SingleSpaMfeContainer from '../MicroFrontEnd/Component/SingleSpaMfeContainer';
import { ISPM_MFE_CONFIG, ISPM_INTEGRATIONS_MFE_CONFIG } from '../MicroFrontEnd/config/mfeConfigs';
import useIspmFeatureFlag from '../MicroFrontEnd/hooks/useIspmFeatureFlag';
import useIntegrationFeatureFlag from '../MicroFrontEnd/hooks/useIntegrationFeatureFlag';
import { getNavVisibility } from './unifiedNavVisibility';
import { useMfeHostStore } from '../utils/MfeHostStoreContext';
import { MigrationRoutes } from '../features/LegacyMigration';

const PrivateRoute = withRouter(({ component: Component, location, ...rest }) => {
  const Child = (props) => (
    <LayoutWrapper>
      <Component {...props} />
    </LayoutWrapper>
  );
  return <Route render={(props) => (isLoggedIn() ? Child(props) : null)} {...rest} />;
});

// Hides headers and footers on match
export const NAV_VIS_CONFIG = {
  base: '/ECMv6',
  header: {
    exact: [
      '/request/privilegedAccess/check-out-credential',
      '/request/privilegedAccess/connect-to-session',
      '/request/privilegedAccess/check-out-role',
    ],
    paramPatterns: ['/certification/details'],
  },
  footer: {
    exact: [
      '/request/privilegedAccess/check-out-credential',
      '/request/privilegedAccess/connect-to-session',
      '/request/privilegedAccess/check-out-role',
    ],
    paramPatterns: ['/certification/details'],
  },
};

const RenderHeader = () => {
  const location = useLocation();
  const leftNavGen2Flag = useSelector((state) => state.user.leftNavGen2);
  const isLeftNavGen2Enabled = Boolean(parseInt(leftNavGen2Flag, 10));
  const { hideHeader } = getNavVisibility(location.pathname, NAV_VIS_CONFIG);

  if (hideHeader) return null;
  if (isLeftNavGen2Enabled) return <Layout />;
  return <Header />;
};

const RenderFooter = () => {
  const location = useLocation();
  const leftNavGen2Flag = useSelector((state) => state.user.leftNavGen2);
  const isLeftNavGen2Enabled = Boolean(parseInt(leftNavGen2Flag, 10));
  const { hideFooter } = getNavVisibility(location.pathname, NAV_VIS_CONFIG);

  if (hideFooter) return null;
  if (isLeftNavGen2Enabled) return null;
  return <Footer />;
};

const MfeContainer = () => {
  const { snapshot } = useMfeHostStore();
  const { isHeaderVisible, isFooterVisible } = snapshot.ui.mfeLayout;

  return (
    <>
      {isHeaderVisible && <RenderHeader />}
      <div id="mfe-container">
        <AppLoading isMfe />
      </div>
      {isFooterVisible && <RenderFooter />}
    </>
  );
};

const RenderIspm = ({ location }) => (
  <SingleSpaMfeContainer
    packageName={ISPM_MFE_CONFIG.packageName}
    routePrefix={ISPM_MFE_CONFIG.routePrefix}
    containerId={ISPM_MFE_CONFIG.containerId}
    errorContainerId={ISPM_MFE_CONFIG.errorContainerId}
    location={location}
    RenderHeader={RenderHeader}
    useFeatureFlag={useIspmFeatureFlag}
  />
);

const RenderIntegrations = ({ location }) => (
  <SingleSpaMfeContainer
    packageName={ISPM_INTEGRATIONS_MFE_CONFIG.packageName}
    routePrefix={ISPM_INTEGRATIONS_MFE_CONFIG.routePrefix}
    containerId={ISPM_INTEGRATIONS_MFE_CONFIG.containerId}
    errorContainerId={ISPM_INTEGRATIONS_MFE_CONFIG.errorContainerId}
    location={location}
    RenderHeader={RenderHeader}
    useFeatureFlag={useIspmFeatureFlag}
  />
);

/**
 * Render component for Integration UI MFE
 * Handles feature flag check and renders MFE containers
 */
const RenderIntegrationUi = ({ location }) => {
  const { isMfeEnabled, isLoading, isError } = useIntegrationFeatureFlag();

  if (isLoading) {
    return <AppLoading />;
  }

  if (isError) {
    return <Global404 path={location} />;
  }

  return isMfeEnabled ? (
    <>
      <RenderHeader location={location} />
      <div id="integration-ui-mfe-error" role="alert" aria-live="polite"></div>
      <div
        id="integration-ui-mfe-content"
        role="main"
        aria-label="Integration UI Application"
      ></div>
    </>
  ) : (
    <Global404 path={location} />
  );
};

/**
 * Render component for Agent MFE. Renders MFE containers unconditionally.
 */
const RenderAgent = () => (
  <>
    <RenderHeader />
    <div id="agent-mfe-error" role="alert" aria-live="polite" />
    <div id="agent-mfe-content" role="main" aria-label="Agent Application" />
  </>
);

const RenderAgentGateway = () => (
  <>
    <RenderHeader />
    <div id="agent-gateway-mfe-error" role="alert" aria-live="polite" />
    <div id="agent-gateway-mfe-content" role="main" aria-label="Agent Gateway Application" />
  </>
);

const ExtRouteContent = () => {
  const isIDProofingEnabled = useSelector((state) => state.user.isIDProofingEnabled);
  const externalClassificationInfo = useSelector((state) => state.user.externalClassificationInfo);
  const isExtClassificationEnabled = externalClassificationInfo?.externalClassification === '1';

  const location = useLocation();
  return (
    <>
      {(isExtClassificationEnabled || isIDProofingEnabled) && <RenderHeader />}
      <ExtRoutes key={location.pathname} location={location} />
    </>
  );
};

const RuleRecsRouteContent = () => {
  const location = useLocation();
  return (
    <>
      <RenderHeader />
      <RuleRecRoutes key={location.pathname} location={location} />
    </>
  );
};

const LegacyMigrationRoute = () => (
  <>
    <RenderHeader />
    <MigrationRoutes />
  </>
);

const RedirectRoute = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      isLoggedIn() ? <Redirect to="/request/requestHome" /> : <Component {...props} />
    }
  />
);

RedirectRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
};

class DynamicModules extends React.Component {
  render() {
    const { location } = this.props;
    const loadedCSSClass = location.pathname.indexOf('mySessions') > 0 ? 'extendedTabHeight' : '';
    const loginClass = location.pathname === '/login' ? 'd-none' : '';
    const UserLogoutClass = location.pathname === '/logout' ? 'd-none' : '';
    const SessionTimeoutClass = location.pathname === '/sessiontimeout' ? 'd-none' : '';

    return (
      <div
        className={`dynamicModules ${loadedCSSClass} ${loginClass} ${UserLogoutClass} ${SessionTimeoutClass}`}
      >
        {codegen.require('./dynamicRouteCodegen')}
      </div>
    );
  }
}

DynamicModules.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

const WithRuterModule = withRouter(DynamicModules);
const Routes = (props) => {
  const allowedRoutes = useSelector((state) => state.user.userModules);

  let blockedUrlList = useSelector((state) => state.user.blockedUrlList);

  blockedUrlList = blockedUrlList
    ? blockedUrlList.map((r) => {
        if (r) return r.replace('/ECMv6', '');

        return r;
      })
    : null;
  const blockedNames = useSelector((state) => state.user.blockedNames);
  const domainInfo = useSelector((state) => state.user.domainInfo);
  const [allowedReactRoutes, setAllowedReactRoutes] = useState(['/login']);

  useEffect(() => {
    if (domainInfo && Object.keys(domainInfo).length) {
      initialiseEnv(domainInfo);
    }
  }, [domainInfo]);

  useEffect(() => {
    if (allowedRoutes.length === 0) {
      return;
    }

    const allowedFilteredRoutes = allowedRoutes.map((r) => {
      if (r) {
        return r.replace('/ECMv6', '');
      }

      return r;
    });

    setAllowedReactRoutes([...allowedFilteredRoutes]);
  }, [allowedRoutes]);

  return (
    <Router basename="/ECMv6">
      <Route404
        routes={{
          staticRoutes: allowedReactRoutes,
          dynamicRoutes: [
            '/request/privilegedAccess/launch',
            '/versionpage',
            '/',
            '/pam',
            '/certification',
            '/gen2',
            '/integrations',
            '/migration',
            AAG_SOD_BASENAME,
            // MFE Routes - Add new MFE routes here (without /ECMv6 prefix)
            '/intelligence/ispm',
            '/request/privilegedAccess',
            '/intelligence/integrations',
            '/intelligence/integrations/new',
            '/agent',
            '/agent-gateway',
            '/mfe',
            '/admin',
          ],
          blockedUrlList,
          blockedNames,
        }}
        render={(path) => <Global404 path={path} />}
      >
        <PrivateRoute
          exact
          path="/"
          render={(args) => <Redirect to="/request/requestHome" {...args} />}
        />
        <Redirector />
        <PrivateRoute path="/certification" component={CertsRoutes} />
        <PrivateRoute exact path="/pam" component={RenderHeader} />
        <PrivateRoute path="/request" component={RenderHeader} />
        {/* MFE Routes - More specific routes must come before less specific ones */}
        <PrivateRoute path="/intelligence/ispm" component={RenderIspm} />
        <PrivateRoute path="/intelligence/integrations" component={RenderIntegrations} />
        <PrivateRoute path="/agent" component={RenderAgent} />
        <PrivateRoute path="/integrations" component={RenderIntegrationUi} />
        <PrivateRoute path="/review" component={RenderHeader} />
        <PrivateRoute path="/external" component={ExtRouteContent} />
        <PrivateRoute path="/rule-recommendations" component={RuleRecsRouteContent} />
        <mfaPrompt.Routes />
        <PrivateRoute path="/design" component={RenderHeader} />
        <PrivateRoute path="/control" component={RenderHeader} />
        <PrivateRoute path="/campaign" component={RenderHeader} />
        <PrivateRoute path="/agent" component={RenderAgent} />
        <PrivateRoute path="/agent-gateway" component={RenderAgentGateway} />
        <PrivateRoute path="/request/privilegedAccess" component={PAMRoutes} />
        <PrivateRoute path="/request/jitAccess" component={PAMRoutes} />
        <PrivateRoute path="/identityMatchAndMerge/home" component={RenderHeader} />
        <RedirectRoute exact path="/login" component={Login} />
        <PrivateRoute exact path="/request/requestHome" component={RequestHome} />
        <PrivateRoute exact path="/versionpage" component={VersionPage} />
        <Route exact path="/auth" component={AuthRedirect} />
        <RedirectRoute exact path="/logout" component={UserLogout} />
        <RedirectRoute exact path="/somethingwentwrong" component={SomethingWentWrong} />
        <RedirectRoute exact path="/sessiontimeout" component={SessionTimeout} />
        <WithRuterModule {...props} />
        <Switch>
          <PrivateRoute path="/admin/forms" component={MfeContainer} />
          <PrivateRoute path="/admin/credentials" component={MfeContainer} />
          <PrivateRoute path="/admin/orchestration" component={MfeContainer} />
          <PrivateRoute exact path="/admin/UIBranding" component={BrandingConfig} />
          <PrivateRoute path="/admin" component={Admin} />
        </Switch>
        <PrivateRoute path="/request" component={RenderFooter} />
        <PrivateRoute path="/review" component={RenderFooter} />
        <PrivateRoute path="/design" component={RenderFooter} />
        <PrivateRoute path="/control" component={Control} />
        <PrivateRoute path="/campaign" component={RenderFooter} />
        <PrivateRoute path="/identityMatchAndMerge/home" component={RenderFooter} />
        <PrivateRoute path="/certification/list" component={RenderHeader} />
        <PrivateRoute path={AAG_SOD_BASENAME} component={AAGRoutes} />
        <PrivateRoute exact path={AAG_HEADER_URLS} component={RenderHeader} />
        <Switch>
          {/* MFE's */}
          <PrivateRoute
            path={[
              '/mfe',
              '/forms/:id',
              '/forms/create',
              '/forms/view/:id',
              '/forms/edit/:id',
              '/vault',
              '/integrations',
            ]}
            component={MfeContainer}
          />
        </Switch>
        {/* Different style MFE needs revision (proxy and static import map) */}
        <PrivateRoute path="/intelligence/ispm" component={RenderIspm} />
        <PrivateRoute path="/intelligence/integrations" component={RenderIntegrations} />
        <PrivateRoute path="/migration" component={LegacyMigrationRoute} />
      </Route404>
    </Router>
  );
};

export default Routes;
